import { ProgressBar, Tooltip } from "@fluentui/react-components";
import {
    AcceptIcon,
    Avatar,
    BellIcon,
    Button,
    Dropdown,
    EditIcon,
    Flex,
    InputLabel,
    RetryIcon,
    Table,
    Text,
} from "@fluentui/react-northstar";
import { useQuery } from "react-query";
import styled from "styled-components";
import { _getFactsheets } from "../../api";
import LoaderPage from "../../../../components/loaders/LoaderPage";
import { useEffect, useRef, useState } from "react";
import { DrawerFactsheetDetail } from "./DrawerFactsheetDetail";
import { useSearchParams } from "react-router-dom";

const rtf = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });

const timeAgo = (dateString) => {
    if (!dateString) return "";
    const diff = Math.floor(
        (new Date(dateString).getTime() - Date.now()) / 1000
    );
    const abs = Math.abs(diff);
    if (abs < 60) return "Sauvegardé " + rtf.format(-Math.floor(abs), "second");
    if (abs < 3600)
        return "Sauvegardé " + rtf.format(-Math.floor(abs / 60), "minute");
    if (abs < 86400)
        return "Sauvegardé " + rtf.format(-Math.floor(abs / 3600), "hour");
    if (abs < 2592000)
        return "Sauvegardé " + rtf.format(-Math.floor(abs / 86400), "day");
    if (abs < 31536000)
        return "Sauvegardé " + rtf.format(-Math.floor(abs / 2592000), "month");
    return "Sauvegardé " + rtf.format(-Math.floor(abs / 31536000), "year");
};

const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const CellDate = ({ date, savedAgo }) => {
    return (
        <SFlexDate vAlign="center" column>
            <STextDateValue content={date} />
            <STextDateFrom content={savedAgo} />
        </SFlexDate>
    );
};

export const CellStatus = ({ status }) => {
    return (
        <SFlexPill
            vAlign="center"
            $bg={status?.status_style?.background}
            $color={status?.status_style?.color}
        >
            {status?.status_style?.icon}
            <STextStatus content={status?.header ?? "--"} />
        </SFlexPill>
    );
};

const CellEmployee = ({ full_name = "", image = "", job_title = "/" }) => {
    return (
        <Flex gap="gap.smaller" vAlign="center">
            <Flex>
                <Avatar name={full_name} image={image} />
            </Flex>
            <Flex column>
                <STextFullname content={full_name} />
                <STextJob content={job_title} />
            </Flex>
        </Flex>
    );
};

const CellCompletion = ({ percent = 0 }) => {
    return (
        <Flex column vAlign="center">
            <ProgressBar
                value={percent / 100}
                color={percent === 100 ? "success" : "brand"}
                thickness="large"
            />
            <STextProgressValue content={`${percent}%`} />
        </Flex>
    );
};

const CellOptionalFieldsNotSet = ({ missing_field_names = [] }) => {
    const textRef = useRef(null);
    const [isEllipsisActive, setIsEllipsisActive] = useState(false);

    const checkEllipsis = () => {
        const el = textRef.current;
        if (el) {
            setIsEllipsisActive(el.scrollHeight > el.clientHeight);
        }
    };

    const textContent =
        missing_field_names.length > 0 ? missing_field_names.join(", ") : "--";

    const textNode = (
        <STextOptFields
            ref={textRef}
            content={textContent}
            onMouseEnter={checkEllipsis}
        />
    );

    return (
        <Flex column vAlign="center">
            {isEllipsisActive ? (
                <Tooltip
                    appearance="inverted"
                    content={textContent}
                    relationship="label"
                    withArrow
                >
                    {textNode}
                </Tooltip>
            ) : (
                textNode
            )}
        </Flex>
    );
};

const CellButtonAction = ({ status }) => {
    return (
        <Button
            content={status?.button_action?.text}
            primary
            flat
            size="small"
            style={status?.button_action?.style}
        />
    );
};
const buildStyleRow = {
    cursor: "pointer",
    backgroundColor: "white",
    ":hover": {
        backgroundColor: "rgb(248, 248, 250)",
    },
};

const STATUS = {
    Incompleted: 0,
    Approved: 1,
    Submitted: 2,
    Rejected: 3,
};
const DEFAULT_STATUS_ALL = "all";

export const STATUS_EMPLOYEES = [
    {
        id: 0,
        key: "preonboarding",
        header: "Pré-onboarding",
    },
    {
        id: -3,
        key: "onboarding",
        header: "Onboarding",
    },
    {
        id: 1,
        key: "suivi",
        header: "Suivi",
    },
    {
        id: 6,
        key: "offboarding",
        header: "Offboarding",
    },
];

export const STATUS_DROPDOWN = [
    {
        key: DEFAULT_STATUS_ALL,
        header: "Tous les statuts",
        id: DEFAULT_STATUS_ALL,
    },
    {
        key: STATUS.Incompleted,
        header: "En cours",
        id: STATUS.Incompleted,
        status_style: {
            background: "var(--blue-light)",
            color: "var(--blue)",
            icon: <EditIcon size="smaller" />,
        },
        button_action: {
            text: "Suivre",
            style: {
                background: "var(--bg-card)",
                color: "var(--text)",
                border: "1px solid var(--border-mid)",
            },
        },
    },
    {
        key: STATUS.Approved,
        header: "Validée",
        id: STATUS.Approved,
        status_style: {
            background: "var(--green-light)",
            color: "var(--green-dark)",
            icon: <AcceptIcon size="smaller" />,
        },
        button_action: {
            text: "Consulter",
            style: {
                background: "var(--bg-card)",
                color: "var(--text)",
                border: "1px solid var(--border-mid)",
            },
        },
    },
    {
        key: STATUS.Submitted,
        header: "À valider",
        id: STATUS.Submitted,
        status_style: {
            background: "var(--purple-light)",
            color: "var(--purple)",
            icon: <BellIcon size="smaller" />,
        },
        button_action: {
            text: "Valider / Refuser",
            style: {
                background: "var(--purple)",
                color: "white",
            },
        },
    },
    {
        key: STATUS.Rejected,
        header: "Refusée",
        id: STATUS.Rejected,
        status_style: {
            background: "var(--amber-light)",
            color: "var(--amber)",
            icon: <RetryIcon size="smaller" />,
        },
        button_action: {
            text: "Consulter",
            style: {
                background: "var(--bg-card)",
                color: "var(--text)",
                border: "1px solid var(--border-mid)",
            },
        },
    },
];

const header = {
    key: "header",
    items: [
        {
            content: "DATE",
            key: "date",
        },
        {
            content: "STATUT",
            key: "status",
            styles: {
                maxWidth: "168px",
            },
        },
        {
            content: "COLLABORATEUR",
            key: "employee",
        },
        {
            content: "COMPLÉTUDE",
            key: "completion",
            styles: {
                maxWidth: "190px",
            },
        },
        {
            content: "CHAMPS NON RENSEIGNÉS",
            key: "optionalFields",
        },
        {
            content: "ACTION",
            key: "action",
            styles: {
                maxWidth: "150px",
            },
        },
    ],
};

const rowFactSheetsSkeleton = [
    {
        key: 1,
        items: [
            {
                content: (
                    <LoaderPage
                        size="smallest"
                        labelPosition="end"
                        label="Chargement des fiches ..."
                        styles={{
                            fontStyle: "italic",
                        }}
                    />
                ),
                key: "1-1",
            },
        ],
    },
];

const rowFactSheetsEmptyRows = [
    {
        key: 1,
        items: [
            {
                content: (
                    <Flex hAlign="center" vAlign="center">
                        <Text
                            content="Aucune fiche disponible."
                            style={{ fontStyle: "italic" }}
                        />
                    </Flex>
                ),
                key: "1-1",
            },
        ],
    },
];

const TableFactsheet = ({ agencyId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [factsheetsFiltred, setFactsheetsFiltred] = useState([]);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedUserDetail, setSelectedUserDetail] = useState(null);

    const {
        data: factsheets,
        isLoading,
        isFetching,
    } = useQuery(
        ["factsheets", agencyId, searchParams.toString()],
        () =>
            _getFactsheets({ agencyId, searchParams: searchParams.toString() }),
        {
            refetchOnWindowFocus: false,
            onSuccess: (data) => {
                return setFactsheetsFiltred(data);
            },
        }
    );

    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("us");
        newParams.delete("cs");
        newParams.append("us", -3);
        newParams.append("us", 0);
        newParams.append("cs", 0);
        newParams.append("cs", 1);
        newParams.append("cs", 2);
        newParams.append("cs", 3);
        setSearchParams(newParams);

        return () => {
            setSearchParams({});
        };
    }, []);

    const buildFormatedFactsheetRows = (factsheets = []) => {
        return factsheets.map((factsheet) => {
            const {
                user_id,
                avatar,
                completeness_created_at,
                completeness_percentage,
                completeness_status,
                completeness_updated_at,
                full_name,
                job_title,
                missing_field_names,
            } = factsheet;

            const status = STATUS_DROPDOWN.find(
                (s) => s.id === completeness_status
            );

            return {
                key: user_id,
                items: [
                    {
                        content: (
                            <CellDate
                                date={formatDate(completeness_created_at)}
                                savedAgo={timeAgo(completeness_updated_at)}
                            />
                        ),
                        key: "1-1",
                    },
                    {
                        content: <CellStatus status={status} />,
                        key: "1-2",
                        styles: { maxWidth: "168px" },
                    },
                    {
                        content: (
                            <CellEmployee
                                full_name={full_name}
                                image={avatar}
                                job_title={job_title}
                            />
                        ),
                        truncateContent: true,
                        key: "1-3",
                    },
                    {
                        content: (
                            <CellCompletion percent={completeness_percentage} />
                        ),
                        key: "1-4",
                        styles: {
                            maxWidth: "190px",
                        },
                    },
                    {
                        content: (
                            <CellOptionalFieldsNotSet
                                missing_field_names={missing_field_names}
                            />
                        ),
                        key: "1-5",
                    },
                    {
                        content: <CellButtonAction status={status} />,
                        key: "1-6",
                        styles: {
                            maxWidth: "150px",
                        },
                    },
                ],
                styles: buildStyleRow,
                onClick: (e) => {
                    setSelectedUserDetail({
                        id: user_id,
                        full_name,
                        job_title,
                        image: avatar,
                    });
                    setOpenDrawer(true);
                },
            };
        });
    };

    const defaultCs = STATUS_DROPDOWN.filter((s) => {
        return searchParams.getAll("cs").includes(s.id.toString());
    });
    const defaultUs = STATUS_EMPLOYEES.filter((s) => {
        return searchParams.getAll("us").includes(s.id.toString());
    });

    const isLoadingOrFetching = isLoading || isFetching;
    const formatedFactsheetsRows = isLoadingOrFetching
        ? rowFactSheetsSkeleton
        : factsheetsFiltred.length
        ? buildFormatedFactsheetRows(factsheetsFiltred)
        : rowFactSheetsEmptyRows;

    const onChangeStatus = (_, i) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("cs");
        const value = i.value;
        if (value?.id === DEFAULT_STATUS_ALL) {
            // select all ids
            newParams.append("cs", 0);
            newParams.append("cs", 1);
            newParams.append("cs", 2);
            newParams.append("cs", 3);
        } else {
            newParams.append("cs", value.id);
        }

        return setSearchParams(newParams);
    };

    const onChangeStatusEmployee = (e, i) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("us");
        const value = i.value;
        value.forEach((val) => newParams.append("us", val.id));

        return setSearchParams(newParams);
    };

    return (
        <Flex
            styles={{
                padding: "20px 24px",
                gap: "14px",
            }}
            column
        >
            <DrawerFactsheetDetail
                isOpen={openDrawer}
                setIsOpen={setOpenDrawer}
                selectedUserDetail={selectedUserDetail}
            />
            {/* Filters */}
            <Flex gap="gap.large">
                <Flex column>
                    <SInputLabel content="Statut" />
                    <Dropdown
                        items={STATUS_DROPDOWN}
                        value={
                            defaultCs.length > 1
                                ? STATUS_DROPDOWN.at(0)
                                : defaultCs.at(0)
                        }
                        onChange={onChangeStatus}
                        checkable
                    />
                </Flex>
                <Flex column>
                    <SInputLabel content="Statut des collaborateurs" />
                    <Dropdown
                        items={STATUS_EMPLOYEES}
                        value={defaultUs}
                        onChange={onChangeStatusEmployee}
                        multiple
                    />
                </Flex>
            </Flex>
            {/* Table content */}
            <Flex>
                <STable
                    header={header}
                    rows={formatedFactsheetsRows}
                    aria-label="table fiche employé"
                    variables={{
                        cellContentOverflow: "none",
                    }}
                />
            </Flex>
        </Flex>
    );
};

export default TableFactsheet;

const STextOptFields = styled(Text)`
    font-size: 12px;
    color: var(--amber);
    font-style: italic;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    line-height: 1.4;
`;
const STextProgressValue = styled(Text)`
    font-size: 11px;
    color: var(--blue);
    font-weight: 600;
`;
const STextFullname = styled(Text)`
    font-weight: 600;
    font-size: 13px;
    line-height: 1.2;
`;
const STextJob = styled(Text)`
    font-size: 11px;
    color: var(--text-tert);
    line-height: 1.2;
`;
const SFlexPill = styled(Flex)`
    display: inline-flex;
    align-items: center;

    padding: 3px 10px;
    border-radius: 4px;
    white-space: nowrap;
    gap: 4px;

    background: ${(props) => props.$bg || "var(--blue-light)"};
    color: ${(props) => props.$color || "var(--blue)"};
`;
const STextStatus = styled(Text)`
    font-size: 11px;
    font-weight: 700;
`;
const STextDateValue = styled(Text)`
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
`;
const STextDateFrom = styled(Text)`
    font-size: 10px;
    color: var(--text-tert);
`;
const SFlexDate = styled(Flex)``;
const SInputLabel = styled(InputLabel)`
    font-size: 10px;
    font-weight: 600;
    color: var(--text-tert);
    text-transform: uppercase;
    letter-spacing: 0.06em;
`;
const STable = styled(Table)`
    width: 100%;
    border-collapse: collapse;
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    border: 1px solid var(--border);
    overflow: hidden;
    .ui-table__cell__content {
        width: 100%;
    }
    [role="columnheader"] {
        font-size: 11px;
        font-weight: 600;
        color: var(--text-tert);
        padding: 11px 14px;
        background: var(--bg-secondary);
        white-space: nowrap;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }
    .ui-table__row:not(.ui-table__header) {
        height: 59px;
        cursor: pointer;
    }
`;
const STextHeading = styled(Text)`
    font-size: 15px;
    font-weight: 600;
`;
const STextDescription = styled(Text)`
    font-size: 11px;
    color: var(--text-tert);
`;
